/** New things on the board: items drawn with a tool, frames, arrows, and where a dropped item goes. */
import { toast } from "../../shared/toast";
import type { BoardCtx } from "./context";
import { asPatch, frameAround, normal, rounded, topZ } from "./geometry";
import { kindFor } from "./tools";
import type { BoardPart } from "./types";
import { CLICK_SIZE, FRAME_SIZE } from "./constants";

/** The creating members of the board service. */
export function createPart(ctx: BoardCtx): BoardPart<"create" | "connect" | "placeFor" | "frames"> {
  const { view, actions, kinds, frames } = ctx.deps;
  return {
    frames,
    async create(t, rect, extra = {}) {
      const r = normal(rect);
      const content = ctx.content();
      const drawn = r.w > CLICK_SIZE && r.h > CLICK_SIZE;
      if (t === "frame") {
        // A click: what a GUI pack makes there (UI/UX: a phone's screen), else Miro's frame.
        const own = frames();
        const place = rounded(drawn ? r : { ...r, w: own?.clicked.w ?? FRAME_SIZE.w, h: own?.clicked.h ?? FRAME_SIZE.h });
        const id = `frame-${Math.random().toString(36).slice(2, 8)}`;
        const look = own ? (drawn ? own.drawn : own.clicked.style) : {};
        const parent = frameAround(content, place);
        const members = content.items.filter((i) => {
          const p = i.place;
          return p.x >= place.x && p.y >= place.y && p.x + p.w <= place.x + place.w && p.y + p.h <= place.y + place.h && (!i.node.group || ctx.isFrame(i.node.group));
        });
        const ok = await ctx.send([
          { edit: "add_group", id, name: `${own && !parent ? own.name : "Frame"} ${content.frames.length + 1}`, parent, members: members.map((m) => m.id), note: "" },
          { edit: "update_group", id, patch: { board: asPatch(place), ...(Object.keys(look).length ? { style: look } : {}) } },
        ]);
        if (!ok) return null;
        view.selectGroup(id);
        return id;
      }
      const kind = kindFor(ctx, t);
      if (!kind) return void toast("Turn on a plugin that brings board items (Art) to draw here.", "error"), null;
      const look = kinds.board(kind);
      const usual = look.size;
      const place = rounded({ ...(drawn ? r : { x: r.x - usual.w / 2, y: r.y - usual.h / 2, ...usual }), z: topZ(content) + 1 });
      const frame = frameAround(content, place);
      const name = look.words || (kinds.nodes().find((k) => k.id === kind)?.label ?? kind);
      const ids = await ctx.addAll([{ name, kind, categories: [], board: place, style: { ...look.style }, ...(frame ? { group: frame } : {}), ...extra }]);
      const id = ids?.[0] ?? null;
      if (id) view.selectNode(id);
      return id;
    },
    async connect(from, to) {
      if (from === to) return false;
      const a = ctx.nodeOf(from);
      const b = ctx.nodeOf(to);
      if (!a || !b) return void toast("Arrows join two items (not frames).", "error"), false;
      const kind = kinds.suggest(a.kind, b.kind) ?? kinds.links()[0]?.id;
      if (!kind) return void toast("No kind of arrow is on here.", "error"), false;
      return !!(await actions.addLink(from, kind, to));
    },
    placeFor(kind, at) {
      const usual = kinds.board(kind).size;
      const place = rounded({ x: at.x - usual.w / 2, y: at.y - usual.h / 2, ...usual, z: topZ(ctx.content()) + 1 });
      const frame = frameAround(ctx.content(), place);
      return { board: place, ...(frame ? { group: frame } : {}) };
    },
  };
}
